import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth, useUser } from '@clerk/clerk-react';

import { getApiUrl } from '../../utils/apiUtils';
import { debugWarn } from '../../utils/debug';

const FEEDBACK_OPTIONS = [
  { value: 'bug', label: 'Bug' },
  { value: 'feedback', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const ALLOWED_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const DRAFT_STORAGE_KEY = 'runium_feedback_draft_v1';

const createLocalId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const ATTACHMENT_TAG_OPTIONS = [
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'document', label: 'Document/PDF' },
  { value: 'log', label: 'Log or Console Output' },
  { value: 'video', label: 'Video' },
  { value: 'other', label: 'Other' },
];

const inferAttachmentTag = (mimeType = '') => {
  if (mimeType.startsWith('image/')) {
    return 'screenshot';
  }
  if (mimeType === 'application/pdf') {
    return 'document';
  }
  if (mimeType.startsWith('video/')) {
    return 'video';
  }
  return 'other';
};

const buildClientMeta = ({
  sessionId,
  user,
  includeActivity,
  contextSource,
}) => {
  const windowSafe = typeof window !== 'undefined' ? window : undefined;
  const navigatorSafe = typeof navigator !== 'undefined' ? navigator : undefined;

  return {
    session_id: sessionId,
    clerk_user_id: user?.id,
    user_email: user?.primaryEmailAddress?.emailAddress,
    include_activity: includeActivity,
    generated_at: new Date().toISOString(),
    locale: navigatorSafe?.language,
    timezone_offset_minutes: new Date().getTimezoneOffset(),
    user_agent: navigatorSafe?.userAgent,
    platform: navigatorSafe?.platform,
    viewport: windowSafe
      ? {
          width: windowSafe.innerWidth,
          height: windowSafe.innerHeight,
        }
      : undefined,
    context_source: contextSource,
  };
};

const FeedbackForm = ({
  isOpen,
  onClose,
  defaultType = 'feedback',
  defaultTitle = '',
  contextSource = 'chat',
  recentActions = [],
  activeConversationId = null,
  onboardingComplete = false,
  experimentAssignments = {},
}) => {
  const { getToken, sessionId } = useAuth();
  const { user } = useUser();

  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef(new Set());

  const loadDraft = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw);
    } catch (error) {
      debugWarn('Unable to parse feedback draft:', error);
      return null;
    }
  }, []);

  const clearDraft = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (error) {
      debugWarn('Unable to clear feedback draft:', error);
    }
  }, []);

  const saveDraft = useCallback((values) => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const payload = {
        feedback_type: values.feedback_type,
        title: values.title,
        description: values.description,
        include_activity: values.include_activity,
      };
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      debugWarn('Unable to persist feedback draft:', error);
    }
  }, []);

  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [submissionResult, setSubmissionResult] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setFocus,
    formState: { errors },
  } = useForm({
    defaultValues: {
      feedback_type: defaultType,
      title: defaultTitle,
      description: '',
      include_activity: true,
    },
  });

  const includeActivityValue = watch('include_activity');
  const descriptionValue = watch('description');

  const resetFormState = useCallback(() => {
    const draft = loadDraft();

    reset({
      feedback_type: draft?.feedback_type || defaultType,
      title: draft?.title ?? defaultTitle,
      description: draft?.description ?? '',
      include_activity: draft?.include_activity ?? true,
    });
    setAttachments([]);
    setErrorMessage('');
    setNoticeMessage('');
    setSubmissionResult(null);
  }, [defaultTitle, defaultType, loadDraft, reset]);

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('overflow-hidden');
      return;
    }

    resetFormState();
    document.body.classList.add('overflow-hidden');

    const focusTimeout = setTimeout(() => {
      setFocus('title');
    }, 0);

    return () => {
      clearTimeout(focusTimeout);
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen, resetFormState, setFocus]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'Tab' && modalRef.current) {
        const focusableSelectors =
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
        const focusable = modalRef.current.querySelectorAll(focusableSelectors);
        if (!focusable.length) {
          return;
        }

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const subscription = watch((values) => {
      saveDraft(values);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isOpen, watch, saveDraft]);

  const addFiles = useCallback((fileList) => {
    const incomingFiles = Array.from(fileList || []);
    if (!incomingFiles.length) {
      return;
    }

    let localWarnings = [];

    setAttachments((previous) => {
      const slotsRemaining = MAX_ATTACHMENTS - previous.length;
      if (slotsRemaining <= 0) {
        localWarnings.push(`You can upload up to ${MAX_ATTACHMENTS} attachments.`);
        return previous;
      }

      const accepted = [];
      for (const file of incomingFiles) {
        if (accepted.length >= slotsRemaining) {
          break;
        }

        const isDuplicate = previous.some(
          (item) => item.file.name === file.name && item.file.size === file.size
        );
        const isDuplicateInBatch = accepted.some(
          (item) => item.file.name === file.name && item.file.size === file.size
        );

        if (isDuplicate || isDuplicateInBatch) {
          localWarnings.push(`${file.name} has already been added.`);
          continue;
        }

        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
          localWarnings.push(
            `${file.name} is not a supported type (PNG, JPG, or PDF only).`
          );
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          localWarnings.push(`${file.name} is larger than 5 MB and was skipped.`);
          continue;
        }

        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null;
        if (previewUrl) {
          previewUrlsRef.current.add(previewUrl);
        }

        accepted.push({
          id: createLocalId(),
          file,
          status: 'ready',
          tag: inferAttachmentTag(file.type),
          previewUrl,
        });
      }

      if (incomingFiles.length > slotsRemaining) {
        localWarnings.push(
          `Only the first ${slotsRemaining} file(s) were added (max ${MAX_ATTACHMENTS}).`
        );
      }

      if (!accepted.length) {
        return previous;
      }

      return [...previous, ...accepted];
    });

    if (localWarnings.length) {
      setNoticeMessage(localWarnings.join(' '));
    } else {
      setNoticeMessage('');
    }
  }, []);

  const handleFileInputChange = useCallback(
    (event) => {
      addFiles(event.target.files);
      event.target.value = '';
    },
    [addFiles]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isOnline) {
        return;
      }
      addFiles(event.dataTransfer.files);
    },
    [addFiles, isOnline]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const removeAttachment = useCallback((attachmentId) => {
    setAttachments((previous) => {
      const target = previous.find((item) => item.id === attachmentId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current.delete(target.previewUrl);
      }
      return previous.filter((item) => item.id !== attachmentId);
    });
  }, []);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  const handleAttachmentTagChange = useCallback((attachmentId, nextTag) => {
    setAttachments((previous) =>
      previous.map((item) =>
        item.id === attachmentId ? { ...item, tag: nextTag } : item
      )
    );
  }, []);

  const attachmentStatuses = useMemo(
    () =>
      attachments.map((item) => ({
        id: item.id,
        name: item.file.name,
        size: item.file.size,
        status: item.status,
        error: item.error,
        previewUrl: item.previewUrl,
      })),
    [attachments]
  );

  const handleNewSubmission = useCallback(() => {
    clearDraft();
    resetFormState();
  }, [clearDraft, resetFormState]);

  const onSubmit = handleSubmit(async (values) => {
    if (isSubmitting) {
      return;
    }

    if (!isOnline) {
      setErrorMessage('You appear to be offline. Please reconnect to submit feedback.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setNoticeMessage('');
    setSubmissionResult(null);

    setAttachments((previous) =>
      previous.map((item) => ({
        ...item,
        status: 'uploading',
        error: undefined,
      }))
    );

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Unable to authenticate your request. Please sign in again.');
      }

      const formData = new FormData();
      formData.append('feedback_type', values.feedback_type);
      formData.append('title', values.title);
      formData.append('description', values.description);
      formData.append('include_activity', String(values.include_activity));

      const clientMeta = buildClientMeta({
        sessionId,
        user,
        includeActivity: values.include_activity,
        contextSource,
      });

      formData.append('client_meta', JSON.stringify(clientMeta));
      const windowSafe = typeof window !== 'undefined' ? window : undefined;
      const navigatorSafe = typeof navigator !== 'undefined' ? navigator : undefined;
      const connection = navigatorSafe?.connection || navigatorSafe?.mozConnection || navigatorSafe?.webkitConnection;
      const contextBlob = {
        ui: {
          view: contextSource,
          recent_actions: recentActions.slice(-5),
          defaults: {
            feedback_type: values.feedback_type,
            title: values.title,
          },
          active_conversation_id: activeConversationId,
        },
        account: {
          tier: user?.publicMetadata?.accountTier || user?.unsafeMetadata?.accountTier || 'beta',
          plan: user?.publicMetadata?.plan || undefined,
          onboard_complete: onboardingComplete,
        },
        client: {
          device: navigatorSafe?.platform,
          os: navigatorSafe?.userAgent,
          app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_GIT_SHA || 'dev',
          network: {
            state: isOnline ? 'online' : 'offline',
            effective_type: connection?.effectiveType,
            latency_ms: typeof connection?.rtt === 'number' ? connection.rtt : undefined,
          },
          viewport: windowSafe
            ? { width: windowSafe.innerWidth, height: windowSafe.innerHeight }
            : undefined,
        },
        experiments: experimentAssignments,
        attachments: attachments.map((item) => ({
          filename: item.file.name,
          tag: item.tag || inferAttachmentTag(item.file.type),
        })),
      };

      formData.append('context_blob', JSON.stringify(contextBlob));

      attachments.forEach((item) => {
        formData.append('attachments', item.file);
      });
      if (attachments.length) {
        formData.append(
          'attachment_tags',
          JSON.stringify(
            attachments.map((item) => item.tag || inferAttachmentTag(item.file.type))
          )
        );
      }

      const response = await fetch(`${getApiUrl()}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let detailMessage = 'Failed to submit feedback.';
        try {
          const errorPayload = await response.json();
          if (errorPayload?.detail) {
            detailMessage = Array.isArray(errorPayload.detail)
              ? errorPayload.detail.map((item) => item.msg || item).join(' ')
              : errorPayload.detail;
          }
        } catch (parseError) {
          // Ignore JSON parse errors and use default detail message
        }
        throw new Error(detailMessage);
      }

      const result = await response.json();
      const skippedNames = new Set(
        (result.skipped_attachments || []).map((entry) => entry.split(' (')[0].trim())
      );
      const savedNames = new Set(
        (result.attachments || []).map((attachment) => attachment.filename)
      );

      setAttachments((previous) =>
        previous.map((item) => {
          if (savedNames.has(item.file.name)) {
            return { ...item, status: 'uploaded' };
          }
          if (skippedNames.has(item.file.name)) {
            const skippedDetail = (result.skipped_attachments || []).find((entry) =>
              entry.startsWith(item.file.name)
            );
            return {
              ...item,
              status: 'skipped',
              error: skippedDetail || 'Attachment was skipped',
            };
          }
          return { ...item, status: 'ready' };
        })
      );

      setSubmissionResult(result);
      clearDraft();
    } catch (error) {
      setErrorMessage(error.message || 'Unexpected error while submitting feedback.');
      setAttachments((previous) =>
        previous.map((item) => ({
          ...item,
          status: 'ready',
        }))
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-form-title"
        className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 text-gray-100 shadow-2xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col my-auto"
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
          <div>
            <h2 id="feedback-form-title" className="text-lg font-semibold text-white">
              Share Beta Feedback
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              Help us improve Runium. Your submission includes training profile and context details only when you opt in.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close feedback form"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {submissionResult ? (
            <div className="space-y-6 px-6 py-8 pb-8">
            <div className="rounded-xl border border-blue-600/40 bg-blue-950/40 p-4 text-blue-200">
              <p className="text-base font-medium">Thank you! Your feedback was submitted.</p>
              <p className="mt-2 text-sm text-blue-100/80">
                Reference ID <span className="font-mono text-blue-100">#{submissionResult.id}</span>.
                We appreciate you helping us polish the beta experience.
              </p>
            </div>

            {!!(submissionResult.skipped_attachments || []).length && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-900/30 p-4 text-amber-200">
                <p className="text-sm font-medium">Some attachments were skipped:</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {submissionResult.skipped_attachments.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Back to Coach
              </button>
              <button
                type="button"
                onClick={handleNewSubmission}
                className="rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Submit another response
              </button>
            </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6 px-6 py-8">
            {!isOnline && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-900/30 p-4 text-sm text-amber-100">
                You appear to be offline. Please reconnect to submit feedback.
              </div>
            )}

            {errorMessage && (
              <div className="rounded-xl border border-red-500/40 bg-red-900/30 p-4 text-sm text-red-100">
                {errorMessage}
              </div>
            )}

            {noticeMessage && !errorMessage && (
              <div className="rounded-xl border border-blue-500/30 bg-blue-900/20 p-4 text-sm text-blue-100">
                {noticeMessage}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col space-y-2">
                <span className="text-sm font-medium text-gray-200">Feedback type</span>
                <select
                  {...register('feedback_type', { required: true })}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {FEEDBACK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col space-y-2">
                <span className="text-sm font-medium text-gray-200">Title</span>
                <input
                  {...register('title', {
                    required: 'Please add a short title.',
                    maxLength: { value: 120, message: 'Keep the title under 120 characters.' },
                  })}
                  type="text"
                  placeholder="Issue with this week\'s plan"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.title && (
                  <span className="text-xs text-red-300">{errors.title.message}</span>
                )}
              </label>
            </div>

            <label className="flex flex-col space-y-2">
              <div className="flex items-center justify-between text-sm font-medium text-gray-200">
                <span>Description</span>
                <span className={`text-xs ${(descriptionValue || '').length > 10000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {(descriptionValue || '').length}/10,000
                </span>
              </div>
              <textarea
                {...register('description', {
                  required: 'Please describe the issue or idea.',
                  minLength: { value: 10, message: 'Add a little more detail so we can help.' },
                  maxLength: { value: 10000, message: 'Please keep it under 10,000 characters.' },
                })}
                rows={6}
                placeholder="Share what happened, what you expected, and any steps to reproduce."
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.description && (
                <span className="text-xs text-red-300">{errors.description.message}</span>
              )}
            </label>

            <div>
              <span className="text-sm font-medium text-gray-200">Attachments</span>
              <p className="mt-1 text-xs text-gray-500">
                Optional. Up to {MAX_ATTACHMENTS} files (PNG, JPG, PDF), 5 MB each.
              </p>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`mt-3 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-700 bg-gray-900/60 p-6 text-center transition ${
                  isOnline ? 'hover:border-blue-500 hover:bg-gray-900/80' : 'opacity-60'
                }`}
              >
                <input
                  ref={fileInputRef}
                  id="feedback-attachments"
                  name="feedback-attachments"
                  type="file"
                  accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
                  multiple
                  onChange={handleFileInputChange}
                  className="hidden"
                  disabled={!isOnline || isSubmitting}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6.1 5 5 0 1117 16H7z"
                  />
                </svg>
                <p className="mt-3 text-sm text-gray-300">
                  Drag & drop files here or
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="ml-1 text-blue-400 underline-offset-2 hover:underline"
                    disabled={!isOnline || isSubmitting}
                  >
                    browse
                  </button>
                </p>
              </div>

              {!!attachmentStatuses.length && (
                <ul className="mt-4 space-y-2 text-sm">
                  {attachmentStatuses.map((item) => (
                    <li
                      key={item.id}
                      className="flex flex-col gap-2 rounded-lg border border-gray-800 bg-gray-900 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="w-full">
                        <p className="font-medium text-gray-100">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {(item.size / 1024 / 1024).toFixed(2)} MB ·{' '}
                          {item.status === 'uploading' && 'Uploading...'}
                          {item.status === 'uploaded' && 'Uploaded'}
                          {item.status === 'skipped' && 'Skipped'}
                          {item.status === 'ready' && 'Ready to upload'}
                        </p>
                        {item.error && (
                          <p className="mt-1 text-xs text-amber-300">{item.error}</p>
                        )}
                        {item.previewUrl && (
                          <img
                            src={item.previewUrl}
                            alt={`${item.name} preview`}
                            className="mt-2 h-20 w-auto rounded border border-gray-800 object-contain"
                          />
                        )}
                        <label className="mt-2 flex w-full flex-col text-xs text-gray-400 sm:max-w-xs">
                          <span className="mb-1 uppercase tracking-wide">Attachment tag</span>
                          <select
                            value={attachments.find((entry) => entry.id === item.id)?.tag || 'other'}
                            onChange={(event) => handleAttachmentTagChange(item.id, event.target.value)}
                            className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {ATTACHMENT_TAG_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center justify-end space-x-3">
                        {item.status === 'uploading' && (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                        )}
                        {item.status !== 'uploading' && (
                          <button
                            type="button"
                            onClick={() => removeAttachment(item.id)}
                            className="rounded-full p-1 text-gray-400 transition hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            aria-label={`Remove ${item.name}`}
                            disabled={isSubmitting}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.75 4A1.75 1.75 0 004 5.75v8.5A1.75 1.75 0 005.75 16h8.5A1.75 1.75 0 0016 14.25v-8.5A1.75 1.75 0 0014.25 4h-8.5zm1.5 3a.75.75 0 011.5 0v5a.75.75 0 01-1.5 0V7zm3 0a.75.75 0 011.5 0v5a.75.75 0 01-1.5 0V7z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-start space-x-3">
              <input
                {...register('include_activity')}
                id="include-activity"
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
              />
              <label htmlFor="include-activity" className="text-sm text-gray-300">
                <span className="font-medium text-gray-100">Include recent activity summary</span>
                <span className="block text-xs text-gray-500">
                  Shares your training profile, latest conversation snippet, and the past week of activity to help us triage your report.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 border-t border-gray-800 pt-6 pb-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isOnline}
                className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-blue-900"
              >
                {isSubmitting && (
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                )}
                Submit feedback
              </button>
            </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
